import type {
  ApiErrorBody,
  AcknowledgeReminderResponse,
  BootstrapResponse,
  ConfirmOrderResponse,
  CreateOrderDraftRequest,
  CreateOrderDraftResponse,
  EncounterOrdersResponse,
  EncounterRecordResponse,
  PatientTimelineResponse,
  PatientAssistantQueryRequest,
  PatientAssistantQueryResponse,
  PatientRemindersResponse,
  PreviewVoiceChartingResponse,
  RegisterPatientRequest,
  RegisterPatientResponse,
  SaveClinicalNoteRequest,
  SaveClinicalNoteResponse,
  TransitionResponse,
  RunAiCheckResponse,
  UpdatePatientFlowStageResponse,
} from "../shared/contracts";
import type { ClinicalEventType } from "../shared/clinical";
import type { PatientClinicalAuditResponse } from "../shared/patient-audit";
import { csrfCookieName, isUnsafeHttpMethod, parseCookieHeader } from "../shared/auth";
import type { PatientFlowStage } from "../shared/workflow";

export class ApiError extends Error {
  readonly code: string;
  readonly requestId: string;
  readonly status: number;

  constructor(status: number, body: ApiErrorBody) {
    super(body.error.message);
    this.name = "ApiError";
    this.code = body.error.code;
    this.requestId = body.error.requestId;
    this.status = status;
  }
}


async function requestJson<T>(path: string, init?: RequestInit): Promise<T> {
  const method = String(init?.method ?? "GET").toUpperCase();
  const csrfToken = isUnsafeHttpMethod(method)
    ? parseCookieHeader(document.cookie).get(csrfCookieName)
    : undefined;
  const response = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers: {
      accept: "application/json",
      ...(init?.body ? { "content-type": "application/json" } : {}),
      ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
      ...init?.headers,
    },
  });

  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const fallback: ApiErrorBody = {
      error: {
        code: "HTTP_ERROR",
        message: "서버 요청에 실패했습니다.",
        requestId: response.headers.get("x-request-id") ?? "unknown",
      },
    };
    throw new ApiError(response.status, (body as ApiErrorBody | null) ?? fallback);
  }
  return body as T;
}


export function loadBootstrap(): Promise<BootstrapResponse> {
  return requestJson<BootstrapResponse>("/api/bootstrap");
}

export async function registerPatient(
  input: RegisterPatientRequest,
): Promise<RegisterPatientResponse> {
  const response = await requestJson<RegisterPatientResponse>("/api/receptions", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return response;
}

export async function startEncounter(encounterId: string): Promise<TransitionResponse> {
  const response = await requestJson<TransitionResponse>(
    `/api/encounters/${encodeURIComponent(encounterId)}/start`,
    { method: "POST" },
  );
  return response;
}

export async function completeEncounter(encounterId: string): Promise<TransitionResponse> {
  const response = await requestJson<TransitionResponse>(
    `/api/encounters/${encodeURIComponent(encounterId)}/complete`,
    { method: "POST" },
  );
  return response;
}

export async function loadPatientTimeline(
  patientId: string,
  filters: { text?: string; type?: ClinicalEventType | null } = {},
): Promise<PatientTimelineResponse> {
  const query = new URLSearchParams();
  if (filters.text?.trim()) query.set("q", filters.text.trim());
  if (filters.type) query.set("type", filters.type);
  const suffix = query.size > 0 ? `?${query.toString()}` : "";
  const response = await requestJson<PatientTimelineResponse>(
    `/api/patients/${encodeURIComponent(patientId)}/timeline${suffix}`,
  );
  return response;
}

export function loadPatientAudit(
  patientId: string,
): Promise<PatientClinicalAuditResponse> {
  return requestJson<PatientClinicalAuditResponse>(
    `/api/patients/${encodeURIComponent(patientId)}/audit`,
  );
}

export async function loadEncounterRecord(
  encounterId: string,
): Promise<EncounterRecordResponse> {
  const response = await requestJson<EncounterRecordResponse>(
    `/api/encounters/${encodeURIComponent(encounterId)}/record`,
  );
  return response;
}

export async function saveClinicalNote(
  encounterId: string,
  input: SaveClinicalNoteRequest,
): Promise<SaveClinicalNoteResponse> {
  const response = await requestJson<SaveClinicalNoteResponse>(
    `/api/encounters/${encodeURIComponent(encounterId)}/note`,
    { method: "POST", body: JSON.stringify(input) },
  );
  return response;
}

export async function loadEncounterOrders(
  encounterId: string,
): Promise<EncounterOrdersResponse> {
  const response = await requestJson<EncounterOrdersResponse>(
    `/api/encounters/${encodeURIComponent(encounterId)}/orders`,
  );
  return response;
}

export async function createOrderDraft(
  encounterId: string,
  input: CreateOrderDraftRequest,
): Promise<CreateOrderDraftResponse> {
  const response = await requestJson<CreateOrderDraftResponse>(
    `/api/encounters/${encodeURIComponent(encounterId)}/orders`,
    { method: "POST", body: JSON.stringify(input) },
  );
  return response;
}

export async function confirmOrder(
  orderId: string,
  expectedVersion: number,
): Promise<ConfirmOrderResponse> {
  const response = await requestJson<ConfirmOrderResponse>(
    `/api/orders/${encodeURIComponent(orderId)}/confirm`,
    { method: "POST", body: JSON.stringify({ expectedVersion }) },
  );
  return response;
}

export async function previewVoiceCharting(
  encounterId: string,
  transcript: string,
): Promise<PreviewVoiceChartingResponse> {
  const response = await requestJson<PreviewVoiceChartingResponse>(
    `/api/encounters/${encodeURIComponent(encounterId)}/voice/preview`,
    { method: "POST", body: JSON.stringify({ transcript }) },
  );
  return response;
}

export async function updatePatientFlowStage(
  receptionId: string,
  nextStage: PatientFlowStage,
): Promise<UpdatePatientFlowStageResponse> {
  const response = await requestJson<UpdatePatientFlowStageResponse>(
    `/api/receptions/${encodeURIComponent(receptionId)}/flow-stage`,
    { method: "POST", body: JSON.stringify({ nextStage }) },
  );
  return response;
}

export async function queryPatientAssistant(
  patientId: string,
  input: PatientAssistantQueryRequest,
): Promise<PatientAssistantQueryResponse> {
  const response = await requestJson<PatientAssistantQueryResponse>(
    `/api/patients/${encodeURIComponent(patientId)}/assistant/query`,
    { method: "POST", body: JSON.stringify(input) },
  );
  return response;
}

export async function loadPatientReminders(
  patientId: string,
): Promise<PatientRemindersResponse> {
  const response = await requestJson<PatientRemindersResponse>(
    `/api/patients/${encodeURIComponent(patientId)}/reminders`,
  );
  return response;
}

export async function runAiCheck(encounterId: string): Promise<RunAiCheckResponse> {
  const response = await requestJson<RunAiCheckResponse>(
    `/api/encounters/${encodeURIComponent(encounterId)}/ai-check`,
    { method: "POST" },
  );
  return response;
}

export async function acknowledgeReminder(
  reminderId: string,
): Promise<AcknowledgeReminderResponse> {
  const response = await requestJson<AcknowledgeReminderResponse>(
    `/api/reminders/${encodeURIComponent(reminderId)}/acknowledge`,
    { method: "POST" },
  );
  return response;
}
